import axiosInstance from '../../utils/axios';
import { AppointmentBookingRequest, AppointmentBookingResponse } from '../types/appointmentBooking';
import { TimeSlot } from '../hooks/useAppointmentBooking';

export const appointmentBookingService = {
  getMe: async () => {
    const response = await axiosInstance.get('/api/v1/me');
    return response.data;
  },

  getEvents: async () => {
    const response = await axiosInstance.get('/api/v1/events');
    return response.data;
  },

  bookAppointment: async (bookingData: AppointmentBookingRequest): Promise<AppointmentBookingResponse> => {
    try {      
      // Prepare request data
      const requestData = {
        ...bookingData
      };
      
      // Make sure we have valid strings for both fields
      if (!requestData.appointmentId || typeof requestData.appointmentId !== 'string') {
        requestData.appointmentId = (bookingData.workTypeGroupId || '').toString();
      }
      
      if (!requestData.workTypeGroupId || typeof requestData.workTypeGroupId !== 'string') {
        requestData.workTypeGroupId = (bookingData.appointmentId || '').toString();
      }
      
      // If both are still empty, log an error
      if (!requestData.appointmentId && !requestData.workTypeGroupId) {
        console.error('Both appointmentId and workTypeGroupId are missing or empty');
      }
            
      const response = await axiosInstance.post<AppointmentBookingResponse>(
        '/api/v1/appointments/public',
        requestData
      );
      return response.data;
    } catch (error: any) {
      console.error('Booking API error:', error.response?.data || error.message);
      throw {
        message: error.response?.data?.message || 'Failed to book appointment',
        status: error.response?.status
      };
    }
  },

  
  getAvailableSlots: async (appointmentId: string, showroomOrLocation?: string): Promise<{ data: TimeSlot[] } | TimeSlot[]> => {
    try {
      // Map showroom values to backend enum: Montreal, Royalmount, Toronto
      let showroom = '';
      if (showroomOrLocation) {
        const upperValue = showroomOrLocation.toUpperCase();
        
        // Check for RoyalMount variations (ROYALMOUNT, RoyalMount, etc.)
        if (upperValue.includes('ROYALMOUNT')) {
          showroom = 'Royalmount'; // Exact casing required by backend!
        } 
        // Check for Montreal variations (but not RoyalMount)
        else if (upperValue.includes('MONTREAL')) {
          showroom = 'Montreal';
        }
        // Check for Toronto
        else if (upperValue.includes('TORONTO')) {
          showroom = 'Toronto';
        }
        // If already one of the enum values, use as-is
        else if (['Montreal', 'Royalmount', 'Toronto'].includes(showroomOrLocation)) {
          showroom = showroomOrLocation;
        }
      }
      
      const url = `/api/v1/appointments/available-slots/${appointmentId}${showroom ? `?showroom=${encodeURIComponent(showroom)}` : ''}`;
      const response = await axiosInstance.get(url);
            
      // Handle both formats: direct array or wrapped in data property
      if (Array.isArray(response.data)) {
        // Each slot already has workTypeGroupId directly in it
        // Verify that slots have workTypeGroupId
        response.data.forEach((slot: TimeSlot, index: number) => {
          if (!slot.workTypeGroupId) {
            console.warn(`Warning: Slot at index ${index} is missing workTypeGroupId`);
          }
        });
        
        return response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Each slot already has workTypeGroupId directly in it
        // Verify that slots have workTypeGroupId
        response.data.data.forEach((slot: TimeSlot, index: number) => {
          if (!slot.workTypeGroupId) {
            console.warn(`Warning: Slot at index ${index} is missing workTypeGroupId`);
          }
        });
        
        return response.data;
      } else {
        // If neither format matches, return an empty data array
        return { data: [] };
      }
    } catch (error: any) {
      console.error('Error fetching available slots:', error);
      throw {
        message: error.response?.data?.message || 'Failed to fetch available slots',
        status: error.response?.status
      };
    }
  }
};
